/**
 * Document content sanitizer and renderer.
 *
 * Uses a DEDICATED DOMPurify instance (not the global one used by SearchBar)
 * with a strict allowlist. Document content is LLM-generated and must be
 * treated as hostile — the session token lives in localStorage.
 *
 * Content format is detected from the content itself, not from filetype.
 */
import DOMPurify from 'dompurify';
import { marked } from 'marked';

// Dedicated instance — never touches the global DOMPurify used by SearchBar
const purifier = DOMPurify(window);

// Strict allowlist: no style, class, id, img, svg, iframe, form, input, button
const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'br', 'hr',
    'ul', 'ol', 'li',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'a',
    'strong', 'em', 'b', 'i', 'u',
    'code', 'pre',
    'blockquote',
  ],
  ALLOWED_ATTR: ['href', 'title', 'colspan', 'rowspan'],
  ALLOW_DATA_ATTR: false,
};

// Force all links to open safely in new tab, https/mailto only
purifier.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    const href = node.getAttribute('href') || '';
    // Only allow https: and mailto: protocols
    if (!/^(https:|mailto:)/i.test(href)) {
      node.removeAttribute('href');
      node.setAttribute('title', '[link removed]');
    } else {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  }
});

/**
 * Detect whether content is HTML, markdown, or plain text.
 */
export function detectContentFormat(content) {
  if (!content) return 'text';

  const trimmed = content.trim();

  // HTML: starts with doctype or significant HTML tags in first 500 chars
  if (/^\s*<!DOCTYPE/i.test(trimmed)) return 'html';
  const prefix = trimmed.slice(0, 500);
  if (/<(html|head|body|div|p|h[1-6]|ul|ol|table)\b/i.test(prefix)) return 'html';

  // Markdown: has markdown headings or bold
  if (/(^|\n)#{1,6}\s/.test(trimmed)) return 'markdown';
  if (/\*\*[^*]+\*\*/.test(trimmed)) return 'markdown';

  return 'text';
}

/**
 * Sanitize HTML content through the strict allowlist.
 */
export function sanitizeHtml(html) {
  return purifier.sanitize(html, PURIFY_CONFIG);
}

/**
 * Render markdown to sanitized HTML.
 */
export function renderMarkdown(markdown) {
  const rawHtml = marked.parse(markdown, { breaks: true });
  return sanitizeHtml(rawHtml);
}

/**
 * Process document content for safe rendering.
 * Returns { format, html, text }
 *   - format: 'html' | 'markdown' | 'text'
 *   - html: sanitized HTML string (for html/markdown), null for text
 *   - text: plain text string (for text format), null for html/markdown
 */
export function processDocumentContent(content) {
  if (!content) return { format: 'text', html: null, text: '' };

  const format = detectContentFormat(content);

  switch (format) {
    case 'html':
      return { format, html: sanitizeHtml(content), text: null };
    case 'markdown':
      return { format, html: renderMarkdown(content), text: null };
    case 'text':
    default:
      return { format, html: null, text: content };
  }
}

/**
 * Strip all HTML/markdown to plain text (for clipboard copy).
 */
export function contentToPlainText(content) {
  if (!content) return '';

  let text = content;
  // Remove style/script blocks
  text = text.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  text = text.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
  // Insert newline on block-level tags (before stripping) so adjacent
  // blocks don't fuse: <h1>T</h1><p>We</p> → "T\nWe" not "TWe"
  text = text.replace(/<\/?(h[1-6]|p|div|li|tr|table|blockquote|pre)\b[^>]*>/gi, '\n');
  text = text.replace(/<(br|hr)\s*\/?>/gi, '\n');
  // Remove remaining (inline) HTML tags
  text = text.replace(/<[^>]+>/g, '');
  // Remove markdown headers
  text = text.replace(/^#{1,6}\s*/gm, '');
  // Remove bold/italic — only paired delimiters adjacent to content (Markdown rules).
  // Asterisks: opening * must touch the content (no leading space), closing * likewise.
  // Matches **bold**, *italic*, ***both*** but not `2 * 3 * 4`.
  text = text.replace(/\*{1,3}([^\s*][^*]*[^\s*]|[^\s*])\*{1,3}/g, '$1');
  // Underscores: only strip when delimiters are at word boundaries (not inside identifiers).
  // e.g. ___both___ or __bold__ at edges, but NOT snake_case or @user_name.
  text = text.replace(/(^|\W)_{1,3}([^_]+)_{1,3}(\W|$)/gm, '$1$2$3');
  // Convert links
  text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  // Remove code backticks
  text = text.replace(/`([^`]+)`/g, '$1');
  // Decode HTML entities
  text = text.replace(/&amp;/g, '&');
  text = text.replace(/&lt;/g, '<');
  text = text.replace(/&gt;/g, '>');
  text = text.replace(/&quot;/g, '"');
  text = text.replace(/&#39;/g, "'");
  text = text.replace(/&nbsp;/g, ' ');
  text = text.replace(/&mdash;/g, '\u2014');
  text = text.replace(/&#8212;/g, '\u2014');
  // Normalize runs of blank lines to max two newlines
  text = text.replace(/\n{3,}/g, '\n\n');
  // Normalize horizontal whitespace
  text = text.replace(/ {3,}/g, ' ');
  return text.trim();
}
